import { useEffect, useRef } from "react";
import * as THREE from "three";

// Color groups for amino acid residues — tuned to fit the cyan-blue theme.
const RESIDUE_GROUPS = {
    // Hydrophobic / nonpolar
    A: { color: 0x4a90e2, name: "Ala" },
    V: { color: 0x4a90e2, name: "Val" },
    L: { color: 0x4a90e2, name: "Leu" },
    I: { color: 0x4a90e2, name: "Ile" },
    M: { color: 0x4a90e2, name: "Met" },
    F: { color: 0x5b9cf0, name: "Phe" },
    W: { color: 0x5b9cf0, name: "Trp" },
    P: { color: 0x6fb1ff, name: "Pro" },
    G: { color: 0xaab9c8, name: "Gly" },
    // Polar uncharged
    S: { color: 0x7cd5e8, name: "Ser" },
    T: { color: 0x7cd5e8, name: "Thr" },
    C: { color: 0xffd55a, name: "Cys" },
    Y: { color: 0x88e0c8, name: "Tyr" },
    N: { color: 0x88e0c8, name: "Asn" },
    Q: { color: 0x88e0c8, name: "Gln" },
    // Positively charged (basic)
    K: { color: 0xff8a5a, name: "Lys" },
    R: { color: 0xff8a5a, name: "Arg" },
    H: { color: 0xff8a5a, name: "His" },
    // Negatively charged (acidic)
    D: { color: 0xd95a5a, name: "Asp" },
    E: { color: 0xd95a5a, name: "Glu" },
};

const FALLBACK_RESIDUE = { color: 0x1a1a1a, name: "Xaa" };

// Deterministic seeded pseudo-random — same sequence always yields same 3D shape.
function seededRandom(seed) {
    let s = seed >>> 0;
    return () => {
        s = (s * 1664525 + 1013904223) >>> 0;
        return s / 4294967296;
    };
}

// Hash a string into a 32-bit unsigned integer for the rng seed.
function hashString(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h;
}

// Build residue positions: pseudo-helical backbone with seeded perturbation.
// Constant peptide bond length, ~109.5° tetrahedral-ish bond angle, deterministic.
function buildBackbone(sequence) {
    const seed = hashString(sequence);
    const rng = seededRandom(seed);
    const positions = [];
    const bondLength = 1.0;
    // Start with two anchor positions to define the initial direction.
    positions.push(new THREE.Vector3(0, 0, 0));
    positions.push(new THREE.Vector3(bondLength, 0, 0));
    // Helical pitch + random twist
    let theta = 0;
    for (let i = 2; i < sequence.length; i++) {
        const prev = positions[i - 1];
        const prevPrev = positions[i - 2];
        const direction = new THREE.Vector3().subVectors(prev, prevPrev).normalize();
        // Bend the chain by ~70° (so 180-70=110° interior angle, near tetrahedral)
        const bendAxis = new THREE.Vector3(rng() - 0.5, rng() - 0.5, rng() - 0.5).normalize();
        // Rotate the direction around bendAxis by 70° to introduce a kink
        direction.applyAxisAngle(bendAxis, THREE.MathUtils.degToRad(70));
        // Helical twist around the previous direction
        theta += Math.PI / 3 + (rng() - 0.5) * 0.4;
        const helicalAxis = new THREE.Vector3()
            .subVectors(prev, prevPrev)
            .normalize();
        direction.applyAxisAngle(helicalAxis, theta);
        positions.push(new THREE.Vector3().addVectors(prev, direction.multiplyScalar(bondLength)));
    }
    return positions;
}

export default function SequenceMolecule3D({ sequence, height = 360 }) {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount || !sequence) return;
        const seq = sequence.toUpperCase().replace(/[^A-Z]/g, "");
        if (seq.length < 2) return;

        const width = mount.clientWidth;
        const h = height;

        const scene = new THREE.Scene();
        scene.fog = new THREE.FogExp2(0x040407, 0.045);

        const camera = new THREE.PerspectiveCamera(38, width / h, 0.1, 200);

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2.5));
        renderer.setSize(width, h);
        renderer.setClearColor(0x000000, 0);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.15;
        mount.appendChild(renderer.domElement);

        // Lighting — keep crisp specular highlights
        scene.add(new THREE.AmbientLight(0x66aaff, 0.35));
        const keyLight = new THREE.DirectionalLight(0xffffff, 1.4);
        keyLight.position.set(4, 6, 5);
        scene.add(keyLight);
        const rimLight = new THREE.PointLight(0x1a1a1a, 14, 28, 1.6);
        rimLight.position.set(-4, -2, 4);
        scene.add(rimLight);
        const accent = new THREE.PointLight(0x9b6bff, 7, 28, 1.6);
        accent.position.set(3, -4, -2);
        scene.add(accent);

        const root = new THREE.Group();
        scene.add(root);

        // Build positions and center on origin
        const positions = buildBackbone(seq);
        const center = new THREE.Vector3();
        positions.forEach((p) => center.add(p));
        center.divideScalar(positions.length);
        positions.forEach((p) => p.sub(center));

        // Fit camera distance to the chain's bounding sphere
        let maxDist = 0;
        positions.forEach((p) => {
            const d = p.length();
            if (d > maxDist) maxDist = d;
        });
        const cameraDistance = Math.max(8, maxDist * 2.1 + 4);
        camera.position.set(0, 0, cameraDistance);
        camera.lookAt(0, 0, 0);

        // Geometry — reusable for performance
        const sphereGeo = new THREE.SphereGeometry(0.42, 32, 32);
        const haloGeo = new THREE.SphereGeometry(0.55, 24, 24);
        const cylinderGeo = new THREE.CylinderGeometry(0.08, 0.08, 1, 14, 1, true);

        // Residue spheres
        const residueMeshes = [];
        positions.forEach((pos, i) => {
            const letter = seq[i];
            const info = RESIDUE_GROUPS[letter] || FALLBACK_RESIDUE;
            const mat = new THREE.MeshPhysicalMaterial({
                color: info.color,
                metalness: 0.55,
                roughness: 0.22,
                clearcoat: 0.85,
                clearcoatRoughness: 0.1,
                emissive: new THREE.Color(info.color).multiplyScalar(0.18),
                envMapIntensity: 1.1,
            });
            const mesh = new THREE.Mesh(sphereGeo, mat);
            mesh.position.copy(pos);
            root.add(mesh);

            // Halo
            const haloMat = new THREE.MeshBasicMaterial({
                color: info.color,
                transparent: true,
                opacity: 0.16,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
            });
            const halo = new THREE.Mesh(haloGeo, haloMat);
            halo.position.copy(pos);
            root.add(halo);
            residueMeshes.push(mesh);
        });

        // Peptide bonds as cylinders
        const bondMat = new THREE.MeshPhysicalMaterial({
            color: 0x1a1a1a,
            metalness: 0.6,
            roughness: 0.2,
            emissive: 0x008caa,
            emissiveIntensity: 0.6,
            transparent: true,
            opacity: 0.9,
        });
        for (let i = 0; i < positions.length - 1; i++) {
            const a = positions[i];
            const b = positions[i + 1];
            const mid = new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5);
            const len = a.distanceTo(b);
            const bond = new THREE.Mesh(cylinderGeo, bondMat);
            bond.position.copy(mid);
            bond.scale.set(1, len, 1);
            bond.lookAt(b);
            bond.rotateX(Math.PI / 2);
            root.add(bond);
        }

        // Mouse drag rotation
        let rotateVel = { x: 0, y: 0 };
        let isDragging = false;
        let prevPointer = { x: 0, y: 0 };

        const onPointerDown = (e) => {
            isDragging = true;
            prevPointer = { x: e.clientX, y: e.clientY };
        };
        const onPointerMove = (e) => {
            if (!isDragging) return;
            const dx = e.clientX - prevPointer.x;
            const dy = e.clientY - prevPointer.y;
            rotateVel.y = dx * 0.008;
            rotateVel.x = dy * 0.008;
            prevPointer = { x: e.clientX, y: e.clientY };
            root.rotation.y += rotateVel.y;
            root.rotation.x += rotateVel.x;
        };
        const onPointerUp = () => {
            isDragging = false;
        };

        const canvas = renderer.domElement;
        canvas.style.cursor = "grab";
        canvas.addEventListener("pointerdown", (e) => {
            canvas.style.cursor = "grabbing";
            onPointerDown(e);
        });
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", () => {
            canvas.style.cursor = "grab";
            onPointerUp();
        });

        // Resize
        const onResize = () => {
            const w = mount.clientWidth;
            renderer.setSize(w, h);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
        };
        const resizeObs = new ResizeObserver(onResize);
        resizeObs.observe(mount);

        // Animate — auto rotate when idle, decay drag velocity
        let raf = 0;
        const animate = () => {
            raf = requestAnimationFrame(animate);
            if (!isDragging) {
                root.rotation.y += 0.0035;
                root.rotation.x += 0.0008;
            } else {
                rotateVel.x *= 0.9;
                rotateVel.y *= 0.9;
            }
            renderer.render(scene, camera);
        };
        animate();

        return () => {
            cancelAnimationFrame(raf);
            resizeObs.disconnect();
            window.removeEventListener("pointermove", onPointerMove);
            renderer.dispose();
            sphereGeo.dispose();
            haloGeo.dispose();
            cylinderGeo.dispose();
            bondMat.dispose();
            residueMeshes.forEach((m) => m.material.dispose());
            if (mount.contains(canvas)) mount.removeChild(canvas);
        };
    }, [sequence, height]);

    return (
        <div
            ref={mountRef}
            className="w-full relative bg-white"
            style={{ height: `${height}px` }}
            data-testid="sequence-molecule-3d"
        />
    );
}
